import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

const FREQUENCIES = [
  { label: "Annually", value: 1 },
  { label: "Semi-annually", value: 2 },
  { label: "Quarterly", value: 4 },
  { label: "Monthly", value: 12 },
  { label: "Daily", value: 365 },
];

function CompoundInterestCalculator() {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [frequency, setFrequency] = useState(12);
  const [contribution, setContribution] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setResult(null);
    setError("");
  };

  const calculate = () => {
    const p = parseFloat(principal);
    const r = parseFloat(rate);
    const t = parseFloat(years);
    const pmt = parseFloat(contribution) || 0;
    const n = Number(frequency);

    if (isNaN(p) || isNaN(r) || isNaN(t) || p < 0 || r < 0 || t <= 0) {
      setError("Please enter a starting amount, interest rate, and number of years.");
      setResult(null);
      return;
    }

    const periodicRate = r / 100 / n;
    const periods = n * t;
    const growthFactor = Math.pow(1 + periodicRate, periods);

    // Future value of the starting principal
    let futureValue = p * growthFactor;

    // Future value of regular monthly contributions (compounded monthly)
    if (pmt > 0) {
      const monthlyRate = r / 100 / 12;
      const months = 12 * t;
      futureValue +=
        monthlyRate === 0
          ? pmt * months
          : pmt * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
    }

    const totalDeposited = p + pmt * 12 * t;
    setError("");
    setResult({
      futureValue: futureValue.toFixed(2),
      deposited: totalDeposited.toFixed(2),
      interest: (futureValue - totalDeposited).toFixed(2),
    });
  };

  return (
    <CalculatorPage
      title="Compound Interest Calculator"
      metaDescription="Free compound interest calculator: see how your savings or investments grow over time with compounding, including optional monthly contributions."
      intro={
        <>
          See how your money grows with <strong>compound interest</strong> —
          interest earned on both your deposits and previously earned interest.
          Add an optional monthly contribution to model regular saving.
        </>
      }
      seoContent={
        <>
          <h2>The compound interest formula</h2>
          <p>
            The future value of a lump sum is A = P(1 + r/n)<sup>nt</sup>,
            where P is the starting amount, r the annual rate as a decimal,
            n the number of compounding periods per year, and t the number of
            years. Monthly contributions are treated as a regular deposit at
            the end of each month, compounded monthly.
          </p>
          <h2>Why compounding matters</h2>
          <p>
            With simple interest, $10,000 at 8% earns $800 every year. With
            compounding, each year's interest itself starts earning interest:
            after 20 years the compounded balance is about $46,600 versus
            $26,000 with simple interest. The effect accelerates with time,
            which is why starting early matters more than the exact rate.
          </p>
          <p>
            Results are estimates for planning purposes and assume a constant
            rate — real investment returns vary year to year.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>What's the difference between simple and compound interest?</h3>
          <p>
            Simple interest is calculated only on your original principal, so
            it earns the same amount every period. Compound interest is
            calculated on the principal plus all previously earned interest, so
            the balance grows faster and faster over time.
          </p>
          <h3>How does compounding frequency affect the total?</h3>
          <p>
            The more often interest is compounded — yearly, monthly, or daily —
            the slightly higher the final amount, because interest starts
            earning interest sooner. The difference is modest at low rates but
            becomes more noticeable over long periods.
          </p>
          <h3>Does this account for inflation or taxes?</h3>
          <p>
            No. The figures are nominal — they don't subtract inflation or any
            tax on your returns. Treat the result as a gross estimate and
            reduce it accordingly if you want a real, after-tax picture.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Starting Amount</span>
          <input type="number" value={principal} onChange={onInput(setPrincipal)} placeholder="e.g. 10000" />
        </label>
        <label className="field">
          <span>Annual Interest Rate (%)</span>
          <input type="number" value={rate} onChange={onInput(setRate)} placeholder="e.g. 8" />
        </label>
        <label className="field">
          <span>Years</span>
          <input type="number" value={years} onChange={onInput(setYears)} placeholder="e.g. 20" />
        </label>
        <label className="field">
          <span>Compounding Frequency</span>
          <select
            value={frequency}
            onChange={(e) => {
              setFrequency(e.target.value);
              setResult(null);
            }}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Monthly Contribution (optional)</span>
          <input type="number" value={contribution} onChange={onInput(setContribution)} placeholder="e.g. 200" />
        </label>
      </div>

      <button className="btn" onClick={calculate}>Calculate Growth</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <div className="result result-breakdown">
          <div>
            Future value: <strong>{Number(result.futureValue).toLocaleString()}</strong>
          </div>
          <div>
            Total deposited: <strong>{Number(result.deposited).toLocaleString()}</strong>
          </div>
          <div>
            Interest earned: <strong>{Number(result.interest).toLocaleString()}</strong>
          </div>
        </div>
      )}
    </CalculatorPage>
  );
}

export default CompoundInterestCalculator;
